import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { roundToDecimalPlaces } from '@semios/app-platform-common'
import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import {
  getZoneEvents,
  parseAllZoneEvents,
  type TParsedZoneEvents,
} from 'App/dashboards/_utils/parseZoneEvents'
import { usePlanningDataByProperty } from 'App/dashboards/_utils/usePlanningDataByProperty'
import { sumEventDurationsInWindow } from 'App/irrigation-scheduler/components/Grid/components/IrrigationZoneEmitterRows/components/_utils/sumEventDurationsInWindow'
import { sumPulseEventDurations } from 'App/irrigation-scheduler/components/Grid/components/IrrigationZoneEmitterRows/components/_utils/sumPulseEventDurations'
import { irrigationSchedulerEventsGet } from 'App/irrigation-scheduler/utils/api/irrigationSchedulerEventsGet'
import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import moment from 'moment-timezone'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { apiFetch } from 'utils/apiFetch'
import { captureFrontendError } from 'utils/captureFrontendError'
import { getIsoWithoutTheZ } from 'utils/getIsoWithoutTheZ'
import type { TPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import { getPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import { isAggregatedType } from 'utils/isAggregatedValueType'
import { removeCmOrInFromSoilProbeDepths } from 'utils/removeCmOrInFromSoilProbeDepths'
import { showNotification } from 'utils/showNotification'
import { unitConverter } from 'utils/unitConverter/unitConverter'

// Water Monitoring is a fixed past-7-day view (no date-range picker), so the window is computed here.
const WINDOW_DAYS = 7

const getWindow = () => {
  const timezone = moment.tz.guess()

  return {
    dateFrom: moment
      .tz(timezone)
      .subtract(WINDOW_DAYS - 1, 'days')
      .startOf('day'),
    dateTo: moment.tz(timezone).endOf('day'),
  }
}

// Date-only bounds for planning-data: the past window's start through one day past the next window.
// Points are summed into the past/next windows (property tz) at compute time.
const getPlanningDateBounds = () => ({
  dateFrom: getWindow().dateFrom.format('YYYY-MM-DD'),
  dateTo: moment
    .tz(moment.tz.guess())
    .add(WINDOW_DAYS + 1, 'days')
    .format('YYYY-MM-DD'),
})

// One soil-moisture probe = one probe row under its zone.
export type TWaterMonitoringProbe = {
  lngLat: string
  name: string
  externalVendorNames: TPointExternalVendorNames | undefined
  // Daily root-zone-average soil moisture over the window, for the sparkline (oldest → newest).
  rootZoneSeries: (number | null)[]
  // Timestamp (ISO) for each rootZoneSeries point — used for the expanded chart's x-axis.
  rootZoneDates: string[]
  // Latest reading + its MAD threshold, for the "Current" deficit chip.
  currentValue: number | null
  madValue: number | null
  // Share of the past 30 days' hours in which the probe reported, as a %. Computed server-side by
  // /soil/probe-uptime; null until that fetch loads, or if the probe isn't visible to the user.
  uptimePercent: number | null
}

// One irrigation zone = one zone row, with its probes nested beneath.
export type TWaterMonitoringZone = {
  zoneId: number
  zoneName: string
  probes: TWaterMonitoringProbe[]
  // Zone-level metrics: applied/planned water (scheduler events) + rain/ETc past & next (planning-data).
  appliedVolumeMm: number | null
  appliedDurationMs: number | null
  plannedVolumeMm: number | null
  plannedDurationMs: number | null
  rainPastMm: number | null
  rainNextMm: number | null
  etcPastMm: number | null
  etcNextMm: number | null
  // Rain + ETc are still loading for this zone's property. Their fetch is slower than the others and
  // lands property by property, so the cells show a placeholder instead of reading as "no data".
  planningPending: boolean
}

export type TWaterMonitoringSection = {
  propertyId: number
  propertyName: string
  zones: TWaterMonitoringZone[]
}

// Soil probes that intersect no irrigation zone are surfaced under this synthetic group rather than
// dropped. Whether such orphans should exist (or be presented differently) is a batched data question.
const UNASSIGNED_ZONE_ID = -1

// Zone-level metrics placeholder used before the fetches resolve (every field null → "-").
type TZoneMetrics = Omit<TWaterMonitoringZone, 'zoneId' | 'zoneName' | 'probes'>

const NULL_METRICS: TZoneMetrics = {
  appliedVolumeMm: null,
  appliedDurationMs: null,
  plannedVolumeMm: null,
  plannedDurationMs: null,
  rainPastMm: null,
  rainNextMm: null,
  etcPastMm: null,
  etcNextMm: null,
  planningPending: false,
}

const makeZone = (
  zoneId: number,
  zoneName: string,
  probes: TWaterMonitoringProbe[],
  metrics: TZoneMetrics = NULL_METRICS,
): TWaterMonitoringZone => ({ zoneId, zoneName, probes, ...metrics })

type TProbe = {
  lngLat: string
  name: string
  externalVendorNames: TPointExternalVendorNames | undefined
  defaultDepths: number[]
  zoneIds: number[]
}

// A zone with what's needed to aggregate its applied/planned irrigation: its emitters (+ flow rate),
// from scheduler events. Rain + ETc come per-zone from /irrigation-scheduler/planning-data (keyed by
// zoneId), so the zone no longer carries blocks or a precip station.
type TZoneInfo = {
  zoneId: number
  zoneName: string
  emitters: { emitterType: TEmitterType; mmPerHour: number }[]
}

type TPropertyMonitoring = {
  propertyId: number
  propertyName: string
  zones: TZoneInfo[]
  probes: TProbe[]
  // Property timezone — defines the applied/planned day windows (matches the ICS report).
  timezone: string
  // The property's block ids — passed to planning-data (which returns ETc/rain per zone).
  blockIds: number[]
}

// Build each selected property's zones + soil probes from the store. Probes are points advertising
// `soilMoisture`, mapped to zone(s) via `configuration.soilProbeIntersectingZoneIdsWithEmitters`
// (zoneId prefix). Per zone we gather emitter types (+ flow rate) for applied/planned water; Rain + ETc
// come from planning-data, so no per-zone blocks or precip station are derived here.
const getPropertyMonitoring = (
  properties: NonNullable<ReturnType<typeof fieldAssetStore.getState>['properties']>,
  propertyIds: number[],
): TPropertyMonitoring[] =>
  propertyIds
    .map((propertyId) => {
      const property = properties[propertyId]

      if (!property) return null

      const points = Object.values(property.points ?? {})

      const byZone = new Map<
        number,
        { zoneName: string; emitters: { emitterType: TEmitterType; mmPerHour: number }[] }
      >()

      Object.values(property.irrigationZoneEmitters ?? {}).forEach((emitter) => {
        const zoneId = Number(emitter.irrigationZoneId)
        const emitterType = emitter.emitterType as TEmitterType
        const mmPerHour = emitter.flowRate?.mmPerHour ?? 0
        // Zone display name = the irrigation zone-emitter's name, consistent with the rest of the app
        // (useIrrigationZoneNameDictionary, the scheduler grid).
        const zoneName = emitter.name
        const entry = byZone.get(zoneId)

        if (entry) entry.emitters.push({ emitterType, mmPerHour })
        else byZone.set(zoneId, { zoneName, emitters: [{ emitterType, mmPerHour }] })
      })

      const zones: TZoneInfo[] = [...byZone.entries()]
        .map(([zoneId, { zoneName, emitters }]) => ({ zoneId, zoneName, emitters }))
        .sort((a, b) => a.zoneName.localeCompare(b.zoneName))

      const probes: TProbe[] = points
        .filter((p) => (p.valuesTimeseries ?? []).some((vt) => vt === 'soilMoisture'))
        .map((p) => ({
          lngLat: String(p.lngLat),
          // Same source + fallback as the Hub "Soil Root Zone Average" table (field-asset name).
          name: p.name ?? translate.phrases.banyanApp('Unnamed Station'),
          externalVendorNames: getPointExternalVendorNames({ point: p, deviceKind: 'soil' }),
          defaultDepths: (p.configuration?.soilDefaultProbeDepths ?? []).map((d) =>
            Number(removeCmOrInFromSoilProbeDepths(d)),
          ),
          zoneIds: [
            ...new Set(
              (p.configuration?.soilProbeIntersectingZoneIdsWithEmitters ?? []).map((id) =>
                Number(String(id).split('|||')[0]),
              ),
            ),
          ],
        }))

      return {
        propertyId,
        propertyName: property.propertyName,
        zones,
        probes,
        timezone: property.timezone ?? moment.tz.guess(),
        // Blocks for the planning request. Prefer `property.blocks` (what the ICS report + pest dashboard
        // use — reliably populated by the dashboard's startup load), and fold in the irrigation points'
        // blockIds as a fallback for a warm/partial store. `point.blockIds` alone is NOT reliable on a
        // fresh session, which left planning-data starved → empty ETc/rain.
        blockIds: [
          ...new Set([
            ...Object.keys(property.blocks ?? {}).map(Number),
            ...points.flatMap((p) => p.blockIds ?? []),
          ]),
        ],
      }
    })
    .filter((p) => p !== null)

// Read one probe's soilMoisture response into a daily root-zone-average series + latest value + MAD.
// Per timestamp, average `value.average` across the probe's favorite (default) depths; MAD comes from
// the matching MADDates entry. We deliberately EXCLUDE the prediction series: with a window ending
// today the API returns it as sub-daily forecast points for the rest of today, which would otherwise
// become the latest ("Current") value. The Hub soil table's TODAY column is the daily actual too — its
// prediction lands on future columns, which Water Monitoring doesn't show.
const parseProbeSoil = (
  soilMoisture: unknown,
  defaultDepths: number[],
  lngLat: string,
): {
  rootZoneSeries: (number | null)[]
  rootZoneDates: string[]
  currentValue: number | null
  madValue: number | null
} => {
  const probeSeries = (soilMoisture ?? []) as {
    metadata: { depth: string; MADDates: { startDate: string; soilMoisture: number; lngLat: string }[] }
    timeseries: { timestamp: string; value: unknown }[]
  }[]

  const byTimestamp: Record<string, { values: number[]; madValue: number }> = {}

  probeSeries.forEach((depthSeries) => {
    const depth = depthSeries.metadata?.depth

    if (!defaultDepths.includes(Number(depth))) return

    const madDates = (depthSeries.metadata?.MADDates ?? [])
      .filter((m) => m.lngLat === lngLat)
      .sort((a, b) => +new Date(b.startDate) - +new Date(a.startDate))

    depthSeries.timeseries.forEach((entry) => {
      if (!isAggregatedType(entry.value)) return

      const average = (entry.value as { average: number | null }).average

      if (isNil(average)) return

      const ts = +new Date(entry.timestamp)
      const mad = madDates.find((m) => +new Date(m.startDate) <= ts)?.soilMoisture

      if (!byTimestamp[entry.timestamp]) {
        byTimestamp[entry.timestamp] = { values: [], madValue: mad ? Number(mad) : 70 }
      }

      byTimestamp[entry.timestamp].values.push(average)
    })
  })

  const sortedTimestamps = Object.keys(byTimestamp).sort((a, b) => +new Date(a) - +new Date(b))
  // Round each root-zone average to the AWC unit's decimal places, matching the Hub soil summary.
  const decimalPlaces = unitConverter.soilAwc().defaultNumberOfDecimalPlaces()

  const rootZoneSeries = sortedTimestamps.map((ts) => {
    const { values } = byTimestamp[ts]

    return values.length > 0
      ? roundToDecimalPlaces(values.reduce((s, v) => s + v, 0) / values.length, decimalPlaces)
      : null
  })

  const latestTimestamp = sortedTimestamps[sortedTimestamps.length - 1]
  const latest = latestTimestamp ? byTimestamp[latestTimestamp] : null

  return {
    rootZoneSeries,
    rootZoneDates: sortedTimestamps,
    currentValue: rootZoneSeries.length > 0 ? rootZoneSeries[rootZoneSeries.length - 1] : null,
    madValue: latest ? latest.madValue : null,
  }
}

type TIrrigationWindows = {
  appliedStart: moment.Moment
  appliedEnd: moment.Moment
  plannedStart: moment.Moment
  plannedEnd: moment.Moment
}

// Applied = past 7 days (incl. today); Planned = next 7 days (from tomorrow), in the property's tz.
const getIrrigationWindows = (timezone: string): TIrrigationWindows => {
  const now = moment.tz(timezone)

  return {
    appliedStart: now
      .clone()
      .subtract(WINDOW_DAYS - 1, 'days')
      .startOf('day'),
    appliedEnd: now.clone().endOf('day'),
    plannedStart: now.clone().add(1, 'day').startOf('day'),
    plannedEnd: now.clone().add(WINDOW_DAYS, 'days').endOf('day'),
  }
}

// A zone's applied (past) + planned (next) irrigation from the irrigation-scheduler events — matching
// the ICS "Planned vs Applied" report: durations summed PER EMITTER then added to the zone, so
// simultaneous emitters double-count (as in ICS). Volume = duration(hours) × the emitter's flowRate
// (mm/hour), mirroring ICS computeDailyIrrigation.
const computeZoneIrrigation = (
  zone: TZoneInfo,
  parsedEvents: Record<string, TParsedZoneEvents>,
  windows: TIrrigationWindows,
): Pick<TZoneMetrics, 'appliedVolumeMm' | 'appliedDurationMs' | 'plannedVolumeMm' | 'plannedDurationMs'> => {
  let appliedMinutes = 0
  let plannedMinutes = 0
  let appliedMm = 0
  let plannedMm = 0

  zone.emitters.forEach(({ emitterType, mmPerHour }) => {
    const { appliedEvents, scheduledEvents, scheduledPulseEvents } = getZoneEvents(
      parsedEvents,
      `${zone.zoneId}|||${emitterType}`,
    )

    const aMinutes = sumEventDurationsInWindow(appliedEvents, windows.appliedStart, windows.appliedEnd)

    const pMinutes =
      sumEventDurationsInWindow(scheduledEvents, windows.plannedStart, windows.plannedEnd) +
      sumPulseEventDurations(scheduledPulseEvents, windows.plannedStart, windows.plannedEnd)

    appliedMinutes += aMinutes

    plannedMinutes += pMinutes

    appliedMm += (aMinutes / 60) * mmPerHour

    plannedMm += (pMinutes / 60) * mmPerHour
  })

  return {
    appliedDurationMs: appliedMinutes * 60_000,
    appliedVolumeMm: appliedMm,
    plannedDurationMs: plannedMinutes * 60_000,
    plannedVolumeMm: plannedMm,
  }
}

// Sum a planning-data daily series (ETc or rain, in mm — the unit unitConverter.evapotranspiration /
// .precipitation expect) for the points whose timestamp falls in [startMs, endMs]. Null when the zone
// has no in-window readings (→ "-"); a real 0 stays 0 (→ "0 in").
const sumPlanningSeriesInWindow = (
  series: { timestamp: string; value: number | string | null }[] | undefined,
  startMs: number,
  endMs: number,
): number | null =>
  (series ?? []).reduce<number | null>((acc, point) => {
    const ts = +new Date(point.timestamp)

    // Skip out-of-window and missing values — `point.value === ''` guards against an empty string, which
    // Number('') would otherwise silently coerce to 0.
    if (ts < startMs || ts > endMs || isNil(point.value) || point.value === '') return acc

    const value = Number(point.value)

    return Number.isNaN(value) ? acc : (acc ?? 0) + value
  }, null)

export type TWaterMonitoringResult = {
  sections: TWaterMonitoringSection[]
  loading: boolean
  error: Error | null
  // True while planning-data (Rain + ETc) is still arriving for any selected property. It is separate from
  // `loading` so the soil and irrigation columns show without waiting for the slowest property.
  planningLoading: boolean
  lastFetchedAt: Date | null
  refresh: () => void
}

// Fetches (in parallel, each independently) for every selected property over a fixed past-7-day window:
// daily soilMoisture (sparkline + Current) from /values, Probe Uptime from /soil/probe-uptime, and scheduler
// events (Applied/Planned). Rain + ETc (past & next) come from planning-data, fetched by
// usePlanningDataByProperty. Probes nest under their zones.
export const useWaterMonitoring = ({
  propertyIds,
  refreshToken = 0,
}: {
  propertyIds: number[]
  refreshToken?: number
}): TWaterMonitoringResult => {
  const properties = fieldAssetStore.useSelector((s) => s.properties)
  const [data, setData] = useState<routes.Values.Response | null>(null)
  const [eventsData, setEventsData] = useState<routes.IrrigationSchedulerEventsGet.Response | null>(null)
  // Probe Uptime over the past 30 days, computed in BigQuery by /soil/probe-uptime. Previously this
  // was an HOURLY soilMoisture fetch summed in the browser; a 30-day window made that payload
  // untenable (720 hours x every depth x every probe) for one integer per probe.
  const [uptimeData, setUptimeData] = useState<routes.SoilProbeUptime.Response | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null)
  const [refreshNonce, setRefreshNonce] = useState(0)
  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), [])
  // Content key so a fresh propertyIds array each render doesn't churn the memo (and the fetch).
  const propertyIdsKey = propertyIds.join('|')

  const propertyMonitoring = useMemo(
    () => (properties ? getPropertyMonitoring(properties, propertyIds) : []),
    [properties, propertyIdsKey],
  )

  // Rain + ETc are kept per property: changing the selection fetches only the properties it adds.
  const planningProperties = useMemo(
    () =>
      propertyMonitoring
        .filter((property) => property.zones.length > 0)
        .map((property) => ({
          propertyId: property.propertyId,
          blockIds: property.blockIds,
          zoneIds: property.zones.map((zone) => zone.zoneId),
        })),
    [propertyMonitoring],
  )

  const {
    planningByZoneId,
    pendingPropertyIds,
    lastFetchedAt: planningFetchedAt,
  } = usePlanningDataByProperty({
    properties: planningProperties,
    ...getPlanningDateBounds(),
    refreshKey: `${refreshNonce}|${refreshToken}`,
  })

  useEffect(() => {
    const probeLngLats = [
      ...new Set(propertyMonitoring.flatMap((p) => p.probes.map((probe) => probe.lngLat))),
    ]

    const zoneIds = [...new Set(propertyMonitoring.flatMap((p) => p.zones.map((z) => z.zoneId)))]

    // Nothing soil- or zone-related to fetch.
    if (probeLngLats.length === 0 && zoneIds.length === 0) {
      setData(null)

      setEventsData(null)

      setUptimeData(null)

      setLoading(false)

      setError(null)

      return
    }

    let cancelled = false

    setLoading(true)

    setError(null)

    const { dateFrom, dateTo } = getWindow()

    // /values now carries only soilMoisture (sparkline + Current). Rain + ETc moved to planning-data
    // (below); applied/planned irrigation comes from scheduler events — so no precipitation, ETc, or
    // irrigationZones here.
    const valuesBody = {
      dateFrom: getIsoWithoutTheZ(dateFrom),
      dateTo: getIsoWithoutTheZ(dateTo),
      // Tag the call's source (as pest, Data Export and Property Playback do) so dashboard-driven
      // /values load is attributable in request logging. A declared optional enum on the Values
      // request schema (value-type-definitions/valuesTimeseriesTypes) that the API validates but
      // never consumes — so an invalid value 422s rather than being silently ignored.
      featureReferer: 'DASHBOARD',
      points: {
        lngLats: probeLngLats,
        valuesRequested: { soilMoisture: { preferredAggregationInterval: 'DAILY' } },
      },
    } as routes.Values.Request

    // Scheduler events spanning both the applied (past 7d) and planned (next 7d) windows. Generous
    // bounds in local time; each property's exact windows are clipped per its own tz at compute time.
    const eventsDateFrom = moment
      .tz(moment.tz.guess())
      .subtract(WINDOW_DAYS + 1, 'days')
      .toISOString()

    const eventsDateTo = moment
      .tz(moment.tz.guess())
      .add(WINDOW_DAYS + 1, 'days')
      .toISOString()

    // Probe Uptime (probes only). The server anchors the rolling 30-day window and does the hour
    // counting in BigQuery, so this returns one row per probe rather than a month of hourly series.
    const uptimeBody = { lngLats: probeLngLats } as routes.SoilProbeUptime.Request

    // Which slots actually hit the network, in allSettled order. The rest resolve to null without a
    // request, so they must not count as successes when deciding whether everything failed.
    const attemptedCount = [
      probeLngLats.length > 0, // /values — soil + Current
      zoneIds.length > 0, // scheduler events — Applied/Planned
      probeLngLats.length > 0, // /soil/probe-uptime
    ].filter(Boolean).length

    // allSettled (not all) so one failing fetch degrades only its own columns instead of blanking the
    // whole table — soil + Current come from /values, Applied/Planned from scheduler events, and Probe
    // Uptime from /soil/probe-uptime. Mirrors the Irrigation Scheduler's own fetch: set each slice
    // independently, capture each failure via captureFrontendError, toast if anything failed.
    Promise.allSettled([
      probeLngLats.length > 0 ? apiFetch({ url: '/values', body: valuesBody }) : Promise.resolve(null),
      zoneIds.length > 0
        ? irrigationSchedulerEventsGet({
            irrigationZoneIds: zoneIds,
            dateFrom: eventsDateFrom,
            dateTo: eventsDateTo,
          })
        : Promise.resolve(null),
      probeLngLats.length > 0
        ? apiFetch({ url: '/soil/probe-uptime', body: uptimeBody })
        : Promise.resolve(null),
    ])
      .then((results) => {
        if (cancelled) return

        const [valuesResult, eventsResult, uptimeResult] = results

        setData(
          valuesResult.status === 'fulfilled' ? (valuesResult.value as routes.Values.Response | null) : null,
        )

        setEventsData(
          eventsResult.status === 'fulfilled'
            ? (eventsResult.value as routes.IrrigationSchedulerEventsGet.Response | null)
            : null,
        )

        setUptimeData(
          uptimeResult.status === 'fulfilled'
            ? (uptimeResult.value as routes.SoilProbeUptime.Response | null)
            : null,
        )

        results.forEach((result) => {
          if (result.status === 'rejected') {
            captureFrontendError(result.reason, { extra: { message: 'Water Monitoring fetch failed' } })
          }
        })

        const rejectedCount = results.filter((result) => result.status === 'rejected').length

        if (rejectedCount > 0) {
          showNotification({ type: 'error', message: translate.phrases.banyanApp('There was an error') })
        }

        // Only a total failure sets `error`. allSettled is here so one bad fetch degrades its own
        // columns rather than the table, so a partial failure must keep rendering what did load — but
        // when every request failed there is nothing to degrade to, and the table's "No data to
        // display" would report an outage as an empty property.
        if (attemptedCount > 0 && rejectedCount === attemptedCount) {
          setError(new Error('Every Water Monitoring fetch failed'))
        }

        setLastFetchedAt(new Date())
      })
      .catch((err) => {
        // allSettled never rejects, so this only catches a throw from the handler above — without it
        // that surfaces as an unhandled rejection and the table sits on stale data.
        if (cancelled) return

        captureFrontendError(err, { extra: { message: 'Water Monitoring result handling failed' } })

        setError(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [propertyMonitoring, refreshNonce, refreshToken])

  // Parsed once per fetch, not once per planning result: moment instants are absolute and only each
  // property's window bounds use its own tz, so the parse tz here doesn't affect the sums.
  const parsedEvents = useMemo(() => parseAllZoneEvents(eventsData, moment.tz.guess()), [eventsData])

  // Soil rows per property, kept out of `sections` so a planning result doesn't parse every probe again.
  const probesByProperty = useMemo(() => {
    // Probe Uptime arrives per probe from /soil/probe-uptime — already a percent over the server's
    // 30-day window, so there's no denominator to derive here.
    const uptimeByLngLat = new Map((uptimeData?.probes ?? []).map((p) => [p.lngLat, p.uptimePercent]))

    return new Map(
      propertyMonitoring.map((property) => [
        property.propertyId,
        new Map(
          property.probes.map((probe) => {
            const soil = parseProbeSoil(
              data?.points?.[probe.lngLat]?.values?.soilMoisture,
              probe.defaultDepths,
              probe.lngLat,
            )

            // Null until the uptime fetch loads. Once loaded, a probe the endpoint returned no row for
            // is one the user can't see soil moisture on — leave it blank rather than claiming 0%.
            const uptimePercent = uptimeData ? (uptimeByLngLat.get(probe.lngLat) ?? null) : null

            return [
              probe.lngLat,
              {
                lngLat: probe.lngLat,
                name: probe.name,
                externalVendorNames: probe.externalVendorNames,
                ...soil,
                uptimePercent,
              },
            ]
          }),
        ),
      ]),
    )
  }, [propertyMonitoring, data, uptimeData])

  const sections = useMemo<TWaterMonitoringSection[]>(() => {
    return propertyMonitoring.map((property) => {
      const windows = getIrrigationWindows(property.timezone)
      // Rain/ETc share the irrigation windows (property tz) so the past/next split lines up with the
      // Applied/Planned columns: past = applied window, next = planned window.
      const pastStartMs = windows.appliedStart.valueOf()
      const pastEndMs = windows.appliedEnd.valueOf()
      const nextStartMs = windows.plannedStart.valueOf()
      const nextEndMs = windows.plannedEnd.valueOf()
      const probesByLngLat = probesByProperty.get(property.propertyId)
      const planningPending = pendingPropertyIds.has(property.propertyId)

      const resolve = (probeList: TProbe[]): TWaterMonitoringProbe[] =>
        probeList
          .map((probe) => probesByLngLat?.get(probe.lngLat))
          .filter((p) => p !== undefined)
          .sort((a, b) => a.name.localeCompare(b.name))

      const zoneIdSet = new Set(property.zones.map((z) => z.zoneId))

      const zones: TWaterMonitoringZone[] = property.zones.map((zone) => {
        // Null (→ "-") until events load; once loaded, a zone with no events sums to 0 (→ "0h"), as ICS.
        const irrigation = eventsData
          ? computeZoneIrrigation(zone, parsedEvents, windows)
          : {
              appliedVolumeMm: null,
              appliedDurationMs: null,
              plannedVolumeMm: null,
              plannedDurationMs: null,
            }

        const zonePlanning = planningByZoneId.get(zone.zoneId)

        return makeZone(
          zone.zoneId,
          zone.zoneName,
          resolve(property.probes.filter((p) => p.zoneIds.includes(zone.zoneId))),
          {
            ...irrigation,
            rainPastMm: sumPlanningSeriesInWindow(zonePlanning?.rain, pastStartMs, pastEndMs),
            rainNextMm: sumPlanningSeriesInWindow(zonePlanning?.rain, nextStartMs, nextEndMs),
            etcPastMm: sumPlanningSeriesInWindow(zonePlanning?.etc, pastStartMs, pastEndMs),
            etcNextMm: sumPlanningSeriesInWindow(zonePlanning?.etc, nextStartMs, nextEndMs),
            planningPending,
          },
        )
      })

      // Probes whose zone(s) aren't among this property's zones (or that list no zone) are orphans —
      // surface them under a synthetic group instead of dropping them.
      const orphanProbes = resolve(property.probes.filter((p) => !p.zoneIds.some((id) => zoneIdSet.has(id))))

      if (orphanProbes.length > 0) {
        // One label for both cases — a property with no zones at all, and orphans on an otherwise
        // zoned property — since from the probe's side they are the same thing: it is in no zone.
        zones.push(makeZone(UNASSIGNED_ZONE_ID, translate.phrases.banyanApp('No Zone Defined'), orphanProbes))
      }

      return { propertyId: property.propertyId, propertyName: property.propertyName, zones }
    })
  }, [propertyMonitoring, probesByProperty, eventsData, parsedEvents, planningByZoneId, pendingPropertyIds])

  // "Last updated" is the most recent successful fetch across the table's own fetches and planning-data.
  const latestFetchedAt =
    planningFetchedAt && (!lastFetchedAt || planningFetchedAt > lastFetchedAt)
      ? planningFetchedAt
      : lastFetchedAt

  const planningLoading = pendingPropertyIds.size > 0

  return useMemo(
    () => ({ sections, loading, planningLoading, error, lastFetchedAt: latestFetchedAt, refresh }),
    [sections, loading, planningLoading, error, latestFetchedAt, refresh],
  )
}
